import {
  boot, buildAttributes, isIntercomAvailable, loadIntercom, show, update,
} from '@usealloy/intercom';

import { isZendeskLoaded, loadZendesk, openZendeskSupportForm } from '~/helpers/zendesk';
import type { Agent, SupportFormOffset } from '~/helpers/zendesk';

/** Unauthenticated Intercom config. Serves the public app ID only -- no secret, no JWT. */
const CONFIG_URL = '/v3/intercom_config/';

interface IntercomConfigResponse {
  /** Null when the flag is off, or the environment has no Intercom credentials. */
  app_id: string | null;
  customer: {
    organization_name: string;
    organization_name_slug: string;
  } | null;
  environment: string;
}

/** Reads the config, or null if the endpoint could not be reached. Never throws. */
const fetchIntercomConfig = async (): Promise<IntercomConfigResponse | null> => {
  try {
    // AjaxOnlyMixin 404s without this header.
    const response = await fetch(CONFIG_URL, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });

    if (!response.ok) return null;

    return await response.json();
  } catch {
    return null;
  }
};

/**
 * Loads the support widget for pages extending daffy/templates/base.html -- the auth flow
 * and the 403/404/500 pages. Intercom or Zendesk, never both.
 *
 * These pages have no agent, so the flag is evaluated anonymously by /v3/intercom_config/.
 * That needs a fetch, which is why neither snippet is a static tag here as it is in
 * v3/templates/dashboard/index.html. Intercom boots without a JWT, creating a Lead that
 * merges into the User record at sign-in.
 */
export const loadPublicSupportWidget = async (): Promise<void> => {
  if (isIntercomAvailable() || isZendeskLoaded()) return;

  const config = await fetchIntercomConfig();

  // Unreachable is not "off" -- falling back to Zendesk here could contradict the flag.
  if (!config) return;

  if (config.app_id) {
    // loadIntercom first: it installs the stub that queues this boot.
    loadIntercom(config.app_id);
    boot(buildAttributes({
      customerName: config.customer?.organization_name,
      customerSlug: config.customer?.organization_name_slug,
      environment: config.environment,
    }));
    return;
  }

  loadZendesk();
};

interface OpenSupportProps {
  /** Prefills the Zendesk form. Absent on the login page, which has no agent. */
  agent?: Agent;
  offsetOverride?: SupportFormOffset;
  positionOverride?: SupportFormOffset;
  sidebarExpanded?: boolean;
}

/**
 * Opens support -- whichever widget this session loaded.
 *
 * `update` doubles as the branch: it refreshes Intercom's context and reports whether that
 * landed. `not-loaded` means Zendesk is live, so fall through to its form; routine during
 * rollout and never an error.
 */
export const openSupport = ({
  agent = { firstName: '', lastName: '', email: '' },
  offsetOverride,
  positionOverride,
  sidebarExpanded = false,
}: OpenSupportProps = {}) => {
  const outcome = update();

  if (outcome === 'not-loaded') {
    openZendeskSupportForm(sidebarExpanded, agent, offsetOverride, positionOverride);
    return;
  }

  // rate-limited: Intercom kept its last context, so opening now would start a conversation
  // pointing at the wrong screen. FRONTEND-719 adds the user-facing reload prompt.
  if (outcome === 'rate-limited') return;

  show();
};

export default {
  loadPublicSupportWidget,
  openSupport,
};
